import { Navigate } from '@tanstack/react-router';

// SECURITY: OnboardingView deactivated -- the finalizeUserOnboarding server
// function it calls has been locked to admin-only. Re-enable when a secure
// onboarding flow is implemented.
// import { OnboardingView } from '@/features/onboarding/components/OnboardingView';
import { NoAssociatedStudentFallback } from '@/features/shared/components/NoAssociatedStudentFallback';
import { useOneRosterAuth } from '@/hooks/useOneRosterAuth';

import { LoadingPage } from '../LoadingPage';

export function AuthenticatedLayout({
  children,
  redirectTo,
  schoolId,
}: {
  children: React.ReactNode;
  redirectTo?: string;
  schoolId?: string;
}) {
  const {
    isAuthenticated,
    isAdmin,
    isSysAdmin,
    isLoading,
    user,
    error,
    isImpersonated,
    isParent,
    isAide,
    children: childUsers,
  } = useOneRosterAuth();

  const hasSomeAideWithAlphaLevelAccess = user?.roles.some(
    (role) => !!role.metadata?.alphaLevelAccess?.length
  );
  const isParentWithoutChildren = isParent && childUsers.length === 0;
  const isAideWithoutStudent =
    isAide && childUsers.length === 0 && !hasSomeAideWithAlphaLevelAccess;

  if (isLoading) {
    return <LoadingPage />;
  }

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  if (!isAuthenticated || !user) {
    // Preserve schoolId when redirecting to login
    const loginUrl = redirectTo ?? '/login';
    const redirectUrl = schoolId
      ? `${loginUrl}?schoolId=${encodeURIComponent(schoolId)}`
      : loginUrl;
    return <Navigate to={redirectUrl} />;
  }

  // SECURITY: Onboarding view deactivated. The finalizeUserOnboarding server
  // function has been locked to admin-only. Uncomment the block below when a
  // secure onboarding flow is implemented.
  //
  // Show onboarding if:
  // 1. User hasn't completed onboarding yet, OR
  // 2. User's primaryOrg doesn't match the schoolId in the URL (and schoolId is provided)
  // 3. User is not a parent or impersonated
  // const shouldShowOnboarding =
  //   !isImpersonated &&
  //   !isSysAdmin &&
  //   !isParent &&
  //   !isAide &&
  //   (!user?.metadata?.timebackOnboarded ||
  //     (schoolId && user?.primaryOrg?.sourcedId !== schoolId));
  //
  // if (shouldShowOnboarding) {
  //   return <OnboardingView user={user} presetSchoolId={schoolId} />;
  // }

  // Show message for parents and aides without students associated trying to access the reports
  if (
    !isAdmin &&
    !isSysAdmin &&
    !isImpersonated &&
    (isParentWithoutChildren || isAideWithoutStudent)
  ) {
    return <NoAssociatedStudentFallback />;
  }

  return children;
}
